import { useRef, useState } from "react";
import { TextB, TextItalic, ListBullets, Link as LinkIcon, Image as ImageIcon, MathOperations, Eye, PencilSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MarkdownContent from "./MarkdownContent";

/**
 * Éditeur de contenu au format Markdown : barre d'outils (gras, italique,
 * titre, liste, lien, formule LaTeX, image), bascule Éditer/Aperçu.
 * L'insertion d'image est optionnelle : fournir `onUploadImage` (fichier
 * -> Promise<url>) pour l'activer dans un contexte donné.
 */
export default function MarkdownEditor({ value, onChange, onUploadImage, placeholder, testIdPrefix = "md" }) {
  const [mode, setMode] = useState("edit");
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const insertAtCursor = (before, after = "", placeholderText = "") => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end) || placeholderText;
    const newValue = value.slice(0, start) + before + selected + after + value.slice(end);
    onChange(newValue);
    requestAnimationFrame(() => {
      ta.focus();
      const cursorPos = start + before.length + selected.length + after.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const handleImageClick = () => fileInputRef.current?.click();
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file || !onUploadImage) return;
    setUploading(true);
    try {
      const url = await onUploadImage(file);
      if (url) insertAtCursor(`![${file.name}](${url})`, "", "");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-border">
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-secondary/40 p-1.5">
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("**", "**", "texte en gras")} title="Gras" data-testid={`${testIdPrefix}-bold`}><TextB size={16} /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("*", "*", "texte en italique")} title="Italique" data-testid={`${testIdPrefix}-italic`}><TextItalic size={16} /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("\n## ", "", "Titre")} title="Titre" data-testid={`${testIdPrefix}-heading`}><span className="text-sm font-bold">H</span></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("\n- ", "", "élément de liste")} title="Liste à puces" data-testid={`${testIdPrefix}-list`}><ListBullets size={16} /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("[", "](https://)", "texte du lien")} title="Lien" data-testid={`${testIdPrefix}-link`}><LinkIcon size={16} /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => insertAtCursor("$", "$", "x^2")} title="Formule mathématique (LaTeX)" data-testid={`${testIdPrefix}-math`}><MathOperations size={16} /></Button>
        {onUploadImage && (
          <Button type="button" size="icon" variant="ghost" onClick={handleImageClick} disabled={uploading} title="Insérer une image" data-testid={`${testIdPrefix}-image-btn`}><ImageIcon size={16} /></Button>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageChange} data-testid={`${testIdPrefix}-image-input`} />
        <div className="ml-auto flex gap-1">
          <Button type="button" size="sm" variant={mode === "edit" ? "default" : "ghost"} className={mode === "edit" ? "bg-primary hover:bg-primary/90" : ""} onClick={() => setMode("edit")} data-testid={`${testIdPrefix}-mode-edit`}>
            <PencilSimple size={14} className="mr-1" /> Éditer
          </Button>
          <Button type="button" size="sm" variant={mode === "preview" ? "default" : "ghost"} className={mode === "preview" ? "bg-primary hover:bg-primary/90" : ""} onClick={() => setMode("preview")} data-testid={`${testIdPrefix}-mode-preview`}>
            <Eye size={14} className="mr-1" /> Aperçu
          </Button>
        </div>
      </div>

      {mode === "edit" ? (
        <Textarea
          ref={textareaRef}
          rows={10}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="rounded-none border-0 font-mono text-sm focus-visible:ring-0"
          data-testid={`${testIdPrefix}-textarea`}
        />
      ) : (
        <div className="max-h-[420px] overflow-y-auto p-4" data-testid={`${testIdPrefix}-preview`}>
          {value ? <MarkdownContent content={value} /> : <p className="text-sm text-muted-foreground">Rien à prévisualiser.</p>}
        </div>
      )}
      {uploading && <p className="border-t border-border px-3 py-1 text-xs text-muted-foreground">Envoi de l'image...</p>}
      <p className="border-t border-border px-3 py-1.5 text-xs text-muted-foreground">
        Formatage <a href="https://www.markdownguide.org/basic-syntax/" target="_blank" rel="noreferrer" className="underline">Markdown</a> · formules entre <code>$...$</code> (ex : <code>$x^2$</code>)
      </p>
    </div>
  );
}
